/**
 * Workset Manager
 * 
 * Manages element ownership, borrowing, and multi-user collaboration.
 */

import { v4 as uuidv4 } from 'uuid';

// ==================== Types ====================

export type WorksetEditable = 'Editable' | 'NotEditable' | 'CheckedOut';

export interface Workset {
    id: string;
    name: string;
    isVisible: boolean;
    isEditable: WorksetEditable;
    owner?: string;  // User who has it checked out
    isDefault: boolean;
    elementCount: number;
}

export interface WorksetUser {
    id: string;
    name: string;
    isLocal: boolean;
    borrowedWorksets: string[];
}

export interface ElementOwnership {
    elementId: string;
    worksetId: string;
    borrowedBy?: string;
    lastModified: number;
    lockedBy?: string;
}

export interface WorksetConflict {
    elementId: string;
    currentOwner: string;
    requestingUser: string;
    timestamp: number;
}

// ==================== Workset Manager ====================

export class WorksetManager {
    private worksets: Map<string, Workset> = new Map();
    private elementOwnership: Map<string, ElementOwnership> = new Map();
    private users: Map<string, WorksetUser> = new Map();
    private conflicts: WorksetConflict[] = [];
    private currentUserId: string = 'local-user';

    constructor() {
        this.createDefaultWorksets();
        this.registerLocalUser();
    }

    private createDefaultWorksets(): void {
        const defaults = [
            { name: 'Shared Levels and Grids', isDefault: true },
            { name: 'Workset1', isDefault: true },
            { name: 'Linked Files', isDefault: false },
        ];

        for (const def of defaults) {
            this.createWorkset(def.name, def.isDefault);
        }
    }

    private registerLocalUser(): void {
        this.users.set(this.currentUserId, {
            id: this.currentUserId,
            name: 'Local User',
            isLocal: true,
            borrowedWorksets: []
        });
    }

    // ==================== Workset Operations ====================

    public createWorkset(name: string, isDefault: boolean = false): Workset {
        const workset: Workset = {
            id: uuidv4(),
            name,
            isVisible: true,
            isEditable: 'Editable',
            isDefault,
            elementCount: 0
        };
        this.worksets.set(workset.id, workset);
        return workset;
    }

    public getWorkset(id: string): Workset | undefined {
        return this.worksets.get(id);
    }

    public getWorksetByName(name: string): Workset | undefined {
        for (const ws of this.worksets.values()) {
            if (ws.name === name) return ws;
        }
        return undefined;
    }

    public getAllWorksets(): Workset[] {
        return Array.from(this.worksets.values());
    }

    public setWorksetVisibility(id: string, visible: boolean): void {
        const workset = this.worksets.get(id);
        if (workset) {
            workset.isVisible = visible;
        }
    }

    public deleteWorkset(id: string, moveToWorksetId: string): boolean {
        const workset = this.worksets.get(id);
        if (!workset || workset.isDefault) return false;

        // Move elements to new workset
        for (const [, ownership] of this.elementOwnership) {
            if (ownership.worksetId === id) {
                ownership.worksetId = moveToWorksetId;
            }
        }

        this.worksets.delete(id);
        this.updateElementCounts();
        return true;
    }

    // ==================== Element Assignment ====================

    public assignToWorkset(elementId: string, worksetId: string): void {
        const existing = this.elementOwnership.get(elementId);

        if (existing) {
            existing.worksetId = worksetId;
            existing.lastModified = Date.now();
        } else {
            this.elementOwnership.set(elementId, {
                elementId,
                worksetId,
                lastModified: Date.now()
            });
        }

        this.updateElementCounts();
    }

    public getElementWorkset(elementId: string): Workset | undefined {
        const ownership = this.elementOwnership.get(elementId);
        return ownership ? this.worksets.get(ownership.worksetId) : undefined;
    }

    public getElementsByWorkset(worksetId: string): string[] {
        const elements: string[] = [];
        for (const [elemId, ownership] of this.elementOwnership) {
            if (ownership.worksetId === worksetId) {
                elements.push(elemId);
            }
        }
        return elements;
    }

    private updateElementCounts(): void {
        for (const workset of this.worksets.values()) {
            workset.elementCount = 0;
        }
        for (const ownership of this.elementOwnership.values()) {
            const workset = this.worksets.get(ownership.worksetId);
            if (workset) {
                workset.elementCount++;
            }
        }
    }

    // ==================== Checkout/Borrowing ====================

    public checkoutWorkset(worksetId: string): boolean {
        const workset = this.worksets.get(worksetId);
        if (!workset) return false;

        if (workset.isEditable !== 'Editable') {
            // Already checked out by someone
            return false;
        }

        workset.isEditable = 'CheckedOut';
        workset.owner = this.currentUserId;

        const user = this.users.get(this.currentUserId);
        if (user) {
            user.borrowedWorksets.push(worksetId);
        }

        console.log(`Workset '${workset.name}' checked out`);
        return true;
    }

    public releaseWorkset(worksetId: string): boolean {
        const workset = this.worksets.get(worksetId);
        if (!workset) return false;

        if (workset.owner !== this.currentUserId) {
            return false;  // Can't release someone else's workset
        }

        workset.isEditable = 'Editable';
        workset.owner = undefined;

        const user = this.users.get(this.currentUserId);
        if (user) {
            user.borrowedWorksets = user.borrowedWorksets.filter(id => id !== worksetId);
        }

        console.log(`Workset '${workset.name}' released`);
        return true;
    }

    public borrowElement(elementId: string): boolean {
        const ownership = this.elementOwnership.get(elementId);
        if (!ownership) return false;

        if (ownership.borrowedBy && ownership.borrowedBy !== this.currentUserId) {
            // Element is borrowed by someone else
            this.conflicts.push({
                elementId,
                currentOwner: ownership.borrowedBy,
                requestingUser: this.currentUserId,
                timestamp: Date.now()
            });
            return false;
        }

        ownership.borrowedBy = this.currentUserId;
        ownership.lastModified = Date.now();
        return true;
    }

    public returnElement(elementId: string): void {
        const ownership = this.elementOwnership.get(elementId);
        if (ownership && ownership.borrowedBy === this.currentUserId) {
            ownership.borrowedBy = undefined;
        }
    }

    // ==================== Conflict Resolution ====================

    public getConflicts(): WorksetConflict[] {
        return [...this.conflicts];
    }

    public resolveConflict(elementId: string, grantToUser: string): void {
        const ownership = this.elementOwnership.get(elementId);
        if (ownership) {
            ownership.borrowedBy = grantToUser;
        }
        this.conflicts = this.conflicts.filter(c => c.elementId !== elementId);
    }

    // ==================== Permissions ====================

    public canEdit(elementId: string): boolean {
        const ownership = this.elementOwnership.get(elementId);
        if (!ownership) return true;  // Unassigned elements are editable

        const workset = this.worksets.get(ownership.worksetId);
        if (!workset) return false;

        // Check workset editability
        if (workset.isEditable === 'NotEditable') return false;
        if (workset.isEditable === 'CheckedOut' && workset.owner !== this.currentUserId) return false;

        // Check element borrowing
        if (ownership.borrowedBy && ownership.borrowedBy !== this.currentUserId) return false;

        return true;
    }

    public canDelete(elementId: string): boolean {
        return this.canEdit(elementId);
    }

    // ==================== Synchronization (Simulated) ====================

    public synchronize(): { received: number; sent: number; conflicts: number } {
        // Simulate sync with central model
        console.log('Synchronizing with central model...');

        const result = {
            received: Math.floor(Math.random() * 10),
            sent: Math.floor(Math.random() * 5),
            conflicts: this.conflicts.length
        };

        console.log(`Sync complete: ${result.received} received, ${result.sent} sent, ${result.conflicts} conflicts`);
        return result;
    }

    public relinquishAll(): void {
        // Release all worksets and elements
        for (const workset of this.worksets.values()) {
            if (workset.owner === this.currentUserId) {
                this.releaseWorkset(workset.id);
            }
        }
        for (const ownership of this.elementOwnership.values()) {
            if (ownership.borrowedBy === this.currentUserId) {
                this.returnElement(ownership.elementId);
            }
        }
    }
}
